import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Search, UserPlus, Lock, Unlock, Eye, Pencil } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PhoneInput, toE164 } from "@/components/ui/phone-input";
import { DEFAULT_COUNTRY } from "@/lib/countries";
import { UserDetailDialog } from "./UserDetailDialog";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { usersApi } from "@/lib/api";
import type { User, UserRole, UserStatus } from "@/lib/api/types";
import { fmtRelativeTime, initials } from "@/lib/utils";
import { ASSIGNABLE_ROLES, ROLE_LABELS } from "@/lib/auth/rbac";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";

const selectClass =
  "cursor-pointer rounded-md border border-border bg-card px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

export default function UsersList() {
  const qc = useQueryClient();

  const {
    q, setQ, page, setPage,
    items, totalItems, totalPages, isFetching,
  } = usePagedQuery<User>({
    key: ["users"],
    fetcher: usersApi.list,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const changeRole = useMutation({
    mutationFn: (v: { id: string; role: UserRole }) => usersApi.changeRole(v.id, v.role),
    onSuccess: invalidate,
  });
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: UserStatus }) => usersApi.setStatus(v.id, v.status),
    onSuccess: invalidate,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [lockId, setLockId] = useState<string | null>(null);
  const [edit, setEdit] = useState<User | null>(null);
  const [detail, setDetail] = useState<User | null>(null);

  return (
    <>
      <PageHeader
        title="Users"
        subtitle={`${totalItems} accounts across customers, doctors, and admins.`}
        action={
          <Button onClick={() => setCreateOpen(true)}>
            <UserPlus className="size-4" /> Add user
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          <div className="border-b border-border p-3">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, phone, or role…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {items.length === 0 ? (
            <EmptyState
              title={isFetching ? "Loading…" : "No users match"}
              description={q ? "Try a different search query." : "No users have signed up yet."}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((u) => {
                  const status = u.status ?? "ACTIVE";
                  const active = status === "ACTIVE";
                  return (
                    <TableRow key={u.id} className="cursor-pointer" onClick={() => setDetail(u)}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="size-9">
                            {u.photoUrl && <AvatarImage src={u.photoUrl} alt={u.name} />}
                            <AvatarFallback>{initials(u.name)}</AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold">{u.name}</p>
                            <p className="truncate text-xs text-muted-foreground">{u.id.slice(0, 8)}…</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{u.email}</p>
                        {u.phoneNumber && (
                          <p className="text-xs text-muted-foreground">{u.phoneNumber}</p>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <select
                          className={selectClass}
                          value={u.role}
                          disabled={changeRole.isPending}
                          onChange={(e) => changeRole.mutate({ id: u.id, role: e.target.value as UserRole })}
                        >
                          {ASSIGNABLE_ROLES.map((r) => (
                            <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>
                          ))}
                        </select>
                      </TableCell>
                      <TableCell>
                        <Badge variant={active ? "success" : "destructive"}>{status}</Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {u.createdAt ? fmtRelativeTime(u.createdAt) : "—"}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => setEdit(u)}>
                            <Pencil className="size-3.5" /> Edit
                          </Button>
                          {status === "SUSPENDED" ? (
                            <Button
                              size="sm" variant="outline"
                              disabled={setStatus.isPending}
                              onClick={() => setStatus.mutate({ id: u.id, status: "ACTIVE" })}
                            >
                              <Unlock className="size-3.5" /> Unlock
                            </Button>
                          ) : (
                            <Button
                              size="sm" variant="outline"
                              disabled={setStatus.isPending}
                              onClick={() => setLockId(u.id)}
                            >
                              <Lock className="size-3.5" /> Lock
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => setDetail(u)}>
                            <Eye className="size-3.5" /> View
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          <Pagination
            page={page}
            size={PAGE_SIZE}
            totalItems={totalItems}
            totalPages={totalPages}
            onChange={setPage}
          />
        </CardContent>
      </Card>

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => { setCreateOpen(false); invalidate(); }}
      />

      <EditUserDialog
        user={edit}
        onOpenChange={(o) => !o && setEdit(null)}
        onSaved={() => { setEdit(null); invalidate(); }}
      />

      <ConfirmDialog
        open={lockId !== null}
        onOpenChange={(o) => !o && setLockId(null)}
        onConfirm={() => {
          if (lockId) setStatus.mutate({ id: lockId, status: "SUSPENDED" });
          setLockId(null);
        }}
        icon={Lock}
        tone="destructive"
        title="Lock this user?"
        description="They keep their data but can't sign in until you unlock the account."
        confirmLabel="Lock"
        loading={setStatus.isPending}
      />

      <UserDetailDialog user={detail} onClose={() => setDetail(null)} />
    </>
  );
}

function CreateUserDialog({
  open, onOpenChange, onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [dialCode, setDialCode] = useState(DEFAULT_COUNTRY.dial);
  const [phoneNum, setPhoneNum] = useState("");
  const [role, setRole] = useState<UserRole>("CUSTOMER");

  const create = useMutation({
    mutationFn: () =>
      usersApi.create({
        name: name.trim(),
        email: email.trim(),
        phoneNumber: toE164(dialCode, phoneNum),
        role,
      }),
    onSuccess: (res) => {
      if (res.data?.code?.endsWith("-00") || res.data?.code === "00") {
        onCreated();
      }
    },
  });

  const serverOk = create.data?.data?.code?.endsWith("-00") || create.data?.data?.code === "00";
  const errored = create.isError || (create.data != null && !serverOk && create.data.data == null);
  const errorMsg = errored ? "Couldn't create the user. Check the details and try again." : null;

  const valid = email.trim().includes("@") && name.trim().length > 0;

  useEffect(() => {
    if (open) {
      setEmail(""); setName(""); setPhoneNum(""); setRole("CUSTOMER");
      setDialCode(DEFAULT_COUNTRY.dial);
    }
  }, [open]);

  return (
    <DialogShell open={open} onOpenChange={onOpenChange} icon={<UserPlus className="size-7" />} title="Add user"
      description="Creates an active account. They sign in with a one-time code.">
      <div className="mt-5 space-y-3">
        <Field label="Full name">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
        </Field>
        <Field label="Email">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" />
        </Field>
        <Field label="Phone (optional)">
          <PhoneInput dialCode={dialCode} number={phoneNum} onDialCode={setDialCode} onNumber={setPhoneNum} />
        </Field>
        <Field label="Role">
          <select
            className={`${selectClass} w-full cursor-pointer px-3 py-2 text-sm`}
            value={role}
            onChange={(e) => setRole(e.target.value as UserRole)}
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>
            ))}
          </select>
        </Field>

        {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}

        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="flex-1" disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? "Creating…" : "Create user"}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}

function EditUserDialog({
  user, onOpenChange, onSaved,
}: {
  user: User | null;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [address, setAddress] = useState("");
  const [gender, setGender] = useState("");
  const [bio, setBio] = useState("");

  useEffect(() => {
    if (user) {
      setName(user.name ?? "");
      setPhotoUrl(user.photoUrl ?? "");
      setAddress(user.address ?? "");
      setGender(user.gender ?? "");
      setBio(user.bio ?? "");
    }
  }, [user]);

  const save = useMutation({
    mutationFn: () =>
      usersApi.update(user!.id, {
        name: name.trim(),
        photoUrl: photoUrl.trim(),
        address: address.trim(),
        gender: gender.trim(),
        bio: bio.trim(),
      }),
    onSuccess: onSaved,
  });

  return (
    <DialogShell open={user !== null} onOpenChange={onOpenChange} icon={<Pencil className="size-7" />} title="Edit user"
      description={user ? user.email : undefined}>
      <div className="mt-5 space-y-3">
        <Field label="Full name">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" />
        </Field>
        <Field label="Photo URL">
          <Input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="https://…" />
        </Field>
        <Field label="Address">
          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="City, Country" />
        </Field>
        <Field label="Gender">
          <select
            className={`${selectClass} w-full cursor-pointer px-3 py-2 text-sm`}
            value={gender}
            onChange={(e) => setGender(e.target.value)}
          >
            <option value="">—</option>
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
            <option value="OTHER">Other</option>
          </select>
        </Field>
        <Field label="Bio">
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={3}
            placeholder="Short bio"
            className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </Field>

        {save.isError && <p className="text-sm text-destructive">Couldn't save changes. Try again.</p>}

        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="flex-1" disabled={save.isPending || name.trim().length === 0} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

/** Shared sweet-alert styled modal shell for the create/edit forms. */
function DialogShell({
  open, onOpenChange, icon, title, description, children,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18, ease: "easeOut" }}
                className="fixed inset-0 z-50 bg-black/45 backdrop-blur-md"
              />
            </DialogPrimitive.Overlay>

            <DialogPrimitive.Content asChild>
              <motion.div
                initial={{ opacity: 0, scale: 0.92, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.94, y: 6 }}
                transition={{ type: "spring", stiffness: 320, damping: 26, mass: 0.7 }}
                className="fixed left-1/2 top-1/2 z-50 max-h-[88vh] w-full max-w-sm -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-3xl border border-border bg-card p-6 shadow-2xl"
              >
                <div className="relative mx-auto flex size-16 items-center justify-center">
                  <span className="absolute inset-0 rounded-full bg-primary/15 blur-md" />
                  <span className="relative flex size-14 items-center justify-center rounded-full bg-primary/15 text-primary">
                    {icon}
                  </span>
                </div>

                <DialogPrimitive.Title asChild>
                  <h2 className="mt-4 text-center text-lg font-bold tracking-tight">{title}</h2>
                </DialogPrimitive.Title>
                {description && (
                  <DialogPrimitive.Description asChild>
                    <p className="mt-1.5 text-center text-sm text-muted-foreground">{description}</p>
                  </DialogPrimitive.Description>
                )}

                {children}
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
